import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateFormatType, formatDate } from "~/common/utils/DateFormatter";
import type {
  FileUploadAttachment,
  FileUploadFeedback,
  FileUploadSelection,
  FileUploadSummaryViewModel,
} from "~/common/components/FileUpload";
import type { ConfiguratorAttachment, ConfiguratorConfiguration } from "../contract/configuration";
import {
  configuratorConfigurationKey,
  configuratorMutationKey,
  type ConfiguratorRuntimeQueryData,
} from "../data/configuratorConfigurationQuery";
import { deleteConfiguratorAttachment, uploadConfiguratorAttachment } from "../data/configuratorAttachmentApi";
import { withConfiguratorMutationLock } from "./configuratorMutationLock";

export const CONFIGURATOR_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

export interface ConfiguratorAttachmentSummary extends FileUploadAttachment {
  fileSizeBytes: number;
  uploadedAt: string | number;
}

export interface ConfiguratorAttachmentSummaryViewModel
  extends Omit<FileUploadSummaryViewModel, "confirmedAttachment"> {
  confirmedAttachment: ConfiguratorAttachmentSummary | null;
}

export interface ConfiguratorAttachmentController {
  viewModel: ConfiguratorAttachmentSummaryViewModel;
  onFilesSelected(selection: FileUploadSelection): void;
  cancelUpload(): void;
  deleteAttachment(): void;
  dismissFeedback(): void;
}

interface UploadVariables {
  controller: AbortController;
  file: File;
  identity: string;
  operationId: number;
}

interface DeleteVariables {
  identity: string;
  operationId: number;
}

interface AttachmentOverride {
  identity: string;
  attachment: ConfiguratorAttachment | null;
}

interface ReconcileVariables {
  identity: string;
}

interface ReconcileResult {
  matched: boolean;
  attachment: ConfiguratorAttachment | null;
}

const makeIdentity = (configuration: ConfiguratorConfiguration) =>
  `${configuration.productCode}\u0000${configuration.configId}`;

const formatBytes = (bytes: number) => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KiB`;
  }
  return `${bytes} B`;
};

const formatUploadedAt = (uploadedAt: string | number) => {
  const date = new Date(uploadedAt);
  return Number.isNaN(date.getTime()) ? String(uploadedAt) : formatDate(date, DateFormatType.DATETIME);
};

const isValidPdfStructure = async (file: File) => {
  const header = new TextDecoder().decode(new Uint8Array(await file.slice(0, 5).arrayBuffer()));
  const tail = await file.slice(Math.max(0, file.size - 1024)).text();
  return header === "%PDF-" && tail.includes("%%EOF");
};

const validationMessage = async (selection: FileUploadSelection, t: TFunction): Promise<string | null> => {
  if (selection.containsDirectory) {
    return t("feature.configurator.v2.attachment.validation.directory", "Folders cannot be uploaded.");
  }
  if (selection.files.length !== 1) {
    return t("feature.configurator.v2.attachment.validation.multiple", "Select exactly one file.");
  }
  const [file] = selection.files;
  if (file.size === 0) {
    return t("feature.configurator.v2.attachment.validation.empty", "PDF must not be empty.");
  }
  if (!file.name.toLowerCase().endsWith(".pdf") || file.type.toLowerCase() !== "application/pdf") {
    return t("feature.configurator.v2.attachment.validation.type", "Select a PDF file.");
  }
  if (file.size > CONFIGURATOR_ATTACHMENT_MAX_BYTES) {
    return t("feature.configurator.v2.attachment.validation.oversized", "Files larger than 10 MiB are not supported.");
  }
  if (!(await isValidPdfStructure(file))) {
    return t("feature.configurator.v2.attachment.validation.invalid", "The selected file is not a valid PDF.");
  }
  return null;
};

const isAbortError = (error: unknown, controller?: AbortController) =>
  controller?.signal.aborted || (error instanceof Error && error.name === "AbortError");

const getHttpStatus = (error: unknown) =>
  typeof error === "object" && error !== null && "status" in error && typeof error.status === "number"
    ? error.status
    : undefined;

const assertCurrentIdentity = (currentIdentity: string, operationIdentity: string) => {
  if (currentIdentity !== operationIdentity) {
    throw new Error("Configurator attachment mutation belongs to a superseded configuration.");
  }
};

export const useConfiguratorAttachmentController = (
  configuration: ConfiguratorConfiguration,
): ConfiguratorAttachmentController => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const identity = makeIdentity(configuration);
  const identityRef = useRef(identity);
  const operationIdRef = useRef(0);
  const uploadControllerRef = useRef<AbortController | null>(null);
  const [pendingOperation, setPendingOperation] = useState<FileUploadSummaryViewModel["pendingOperation"]>(null);
  const [feedback, setFeedback] = useState<FileUploadFeedback | null>(null);
  const [attachmentOverride, setAttachmentOverride] = useState<AttachmentOverride | undefined>(undefined);

  const effectiveAttachment =
    attachmentOverride?.identity === identity ? attachmentOverride.attachment : configuration.attachment;

  const isCurrent = useCallback(
    (operationIdentity: string, operationId: number) =>
      identityRef.current === operationIdentity && operationIdRef.current === operationId,
    [],
  );

  useEffect(() => {
    if (identityRef.current === identity) {
      return;
    }
    identityRef.current = identity;
    operationIdRef.current += 1;
    uploadControllerRef.current?.abort();
    uploadControllerRef.current = null;
    setPendingOperation(null);
    setFeedback(null);
    setAttachmentOverride(undefined);
  }, [identity]);

  useEffect(() => {
    if (attachmentOverride === undefined) {
      return;
    }
    if (attachmentOverride.identity !== identity) {
      return;
    }
    const authoritative = configuration.attachment;
    const overriddenAttachment = attachmentOverride.attachment;
    const overrideMatchesAuthoritative =
      (overriddenAttachment === null && authoritative === undefined) ||
      (overriddenAttachment !== null &&
        authoritative !== undefined &&
        overriddenAttachment.fileName === authoritative.fileName &&
        overriddenAttachment.url === authoritative.url &&
        overriddenAttachment.fileSizeBytes === authoritative.fileSizeBytes &&
        overriddenAttachment.uploadedAt === authoritative.uploadedAt);
    if (overrideMatchesAuthoritative) {
      setAttachmentOverride(undefined);
    }
  }, [attachmentOverride, configuration.attachment, identity]);

  const setCachedAttachment = useCallback(
    (operationIdentity: string, attachment: ConfiguratorAttachment | undefined) => {
      const [productCode, configId] = operationIdentity.split("\u0000");
      queryClient.setQueryData<ConfiguratorRuntimeQueryData>(configuratorConfigurationKey(productCode), (current) => {
        if (!current || current.configuration.configId !== configId) {
          return current;
        }
        return {
          ...current,
          configuration: { ...current.configuration, attachment },
        };
      });
    },
    [queryClient],
  );

  const reconcileMutation = useMutation({
    mutationKey: configuratorMutationKey(configuration.productCode),
    mutationFn: async ({ identity: operationIdentity }: ReconcileVariables): Promise<ReconcileResult> => {
      const [productCode, configId] = operationIdentity.split("\u0000");
      return withConfiguratorMutationLock(configId, async () => {
        assertCurrentIdentity(identityRef.current, operationIdentity);
        await queryClient.refetchQueries(
          { queryKey: configuratorConfigurationKey(productCode), exact: true },
          { throwOnError: true },
        );
        const current = queryClient.getQueryData<ConfiguratorRuntimeQueryData>(
          configuratorConfigurationKey(productCode),
        );
        return {
          matched: current?.configuration.configId === configId,
          attachment: current?.configuration.attachment ?? null,
        };
      });
    },
    retry: false,
  });

  const reconcile = useCallback(
    async (operationIdentity: string, operationId: number) => {
      if (!isCurrent(operationIdentity, operationId)) {
        return;
      }
      setPendingOperation({ type: "reconcile" });
      try {
        const result = await reconcileMutation.mutateAsync({ identity: operationIdentity });
        if (isCurrent(operationIdentity, operationId) && result.matched) {
          setAttachmentOverride({
            identity: operationIdentity,
            attachment: result.attachment,
          });
        }
      } catch {
        if (isCurrent(operationIdentity, operationId)) {
          setFeedback({
            severity: "error",
            message: t(
              "feature.configurator.v2.attachment.reconcileError",
              "Could not refresh the attachment. Try again.",
            ),
          });
        }
      } finally {
        if (isCurrent(operationIdentity, operationId)) {
          setPendingOperation(null);
        }
      }
    },
    [isCurrent, reconcileMutation, t],
  );

  const uploadMutation = useMutation({
    mutationKey: configuratorMutationKey(configuration.productCode),
    mutationFn: ({ controller, file, identity: operationIdentity }: UploadVariables) => {
      const [productCode, configId] = operationIdentity.split("\u0000");
      return withConfiguratorMutationLock(configId, async () => {
        assertCurrentIdentity(identityRef.current, operationIdentity);
        return uploadConfiguratorAttachment(productCode, configId, file, controller.signal);
      });
    },
    retry: false,
  });

  const deleteMutation = useMutation({
    mutationKey: configuratorMutationKey(configuration.productCode),
    mutationFn: ({ identity: operationIdentity }: DeleteVariables) => {
      const [productCode, configId] = operationIdentity.split("\u0000");
      return withConfiguratorMutationLock(configId, () => {
        assertCurrentIdentity(identityRef.current, operationIdentity);
        return deleteConfiguratorAttachment(productCode, configId);
      });
    },
    retry: false,
  });

  const onFilesSelected = useCallback(
    (selection: FileUploadSelection) => {
      if (
        pendingOperation ||
        queryClient.isMutating({ mutationKey: configuratorMutationKey(configuration.productCode) }) > 0
      ) {
        return;
      }
      if (effectiveAttachment) {
        setFeedback({
          severity: "error",
          message: t(
            "feature.configurator.v2.attachment.deleteBeforeReplace",
            "Delete the current attachment before selecting a replacement.",
          ),
        });
        return;
      }
      const selectedFile = selection.files[0];
      const operationIdentity = identity;
      const operationId = ++operationIdRef.current;
      void validationMessage(selection, t).then((message) => {
        if (!isCurrent(operationIdentity, operationId)) {
          return;
        }
        if (message) {
          setFeedback({ severity: "error", message });
          return;
        }
        if (!selectedFile) {
          return;
        }
        const controller = new AbortController();
        uploadControllerRef.current = controller;
        setFeedback(null);
        setPendingOperation({ type: "upload", fileName: selectedFile.name });
        const variables: UploadVariables = { controller, file: selectedFile, identity: operationIdentity, operationId };
        void uploadMutation
          .mutateAsync(variables)
          .then(async (response) => {
            if (!isCurrent(operationIdentity, operationId)) {
              return;
            }
            if (
              !response.success ||
              !response.fileName ||
              !response.url ||
              response.fileSizeBytes === undefined ||
              response.uploadedAt === undefined
            ) {
              await reconcile(operationIdentity, operationId);
              return;
            }
            setCachedAttachment(operationIdentity, {
              fileName: response.fileName,
              url: response.url,
              fileSizeBytes: response.fileSizeBytes,
              uploadedAt: response.uploadedAt,
            });
            setAttachmentOverride({
              identity: operationIdentity,
              attachment: {
                fileName: response.fileName,
                url: response.url,
                fileSizeBytes: response.fileSizeBytes,
                uploadedAt: response.uploadedAt,
              },
            });
            uploadControllerRef.current = null;
            setPendingOperation(null);
          })
          .catch(async (error: unknown) => {
            if (!isCurrent(operationIdentity, operationId)) {
              return;
            }
            if (isAbortError(error, controller)) {
              await reconcile(operationIdentity, operationId);
              return;
            }
            if (getHttpStatus(error) === 409) {
              setFeedback({
                severity: "info",
                message: t(
                  "feature.configurator.v2.attachment.conflict",
                  "An attachment already exists. Refreshing the configuration.",
                ),
              });
              await reconcile(operationIdentity, operationId);
              return;
            }
            setPendingOperation(null);
            setFeedback({
              severity: "error",
              message: t(
                "feature.configurator.v2.attachment.uploadError",
                "The attachment could not be uploaded. Try again.",
              ),
            });
          });
      });
    },
    [
      configuration.productCode,
      effectiveAttachment,
      identity,
      isCurrent,
      pendingOperation,
      queryClient,
      reconcile,
      setCachedAttachment,
      t,
      uploadMutation,
    ],
  );

  const cancelUpload = useCallback(() => {
    if (pendingOperation?.type !== "upload") {
      return;
    }
    uploadControllerRef.current?.abort();
  }, [pendingOperation]);

  const deleteAttachment = useCallback(() => {
    if (
      !effectiveAttachment ||
      pendingOperation ||
      queryClient.isMutating({ mutationKey: configuratorMutationKey(configuration.productCode) }) > 0
    ) {
      return;
    }
    const operationIdentity = identity;
    const operationId = ++operationIdRef.current;
    setFeedback(null);
    setPendingOperation({ type: "delete" });
    void deleteMutation
      .mutateAsync({ identity: operationIdentity, operationId })
      .then(() => {
        if (isCurrent(operationIdentity, operationId)) {
          setCachedAttachment(operationIdentity, undefined);
          setAttachmentOverride({ identity: operationIdentity, attachment: null });
          setPendingOperation(null);
        }
      })
      .catch((error: unknown) => {
        if (!isCurrent(operationIdentity, operationId)) {
          return;
        }
        if (getHttpStatus(error) === 404) {
          setCachedAttachment(operationIdentity, undefined);
          setAttachmentOverride({ identity: operationIdentity, attachment: null });
          setPendingOperation(null);
          return;
        }
        setPendingOperation(null);
        setFeedback({
          severity: "error",
          message: t(
            "feature.configurator.v2.attachment.deleteError",
            "The attachment could not be deleted. Try again.",
          ),
        });
      });
  }, [
    configuration.productCode,
    deleteMutation,
    effectiveAttachment,
    identity,
    isCurrent,
    pendingOperation,
    queryClient,
    setCachedAttachment,
    t,
  ]);

  const confirmedAttachment = useMemo<ConfiguratorAttachmentSummary | null>(() => {
    const attachment = effectiveAttachment;
    if (!attachment) {
      return null;
    }
    return {
      fileName: attachment.fileName,
      previewUrl: attachment.url,
      sizeText: formatBytes(attachment.fileSizeBytes),
      uploadedAtText: formatUploadedAt(attachment.uploadedAt),
      fileSizeBytes: attachment.fileSizeBytes,
      uploadedAt: attachment.uploadedAt,
    };
  }, [effectiveAttachment]);

  return {
    viewModel: {
      confirmedAttachment,
      pendingOperation,
      feedback,
    },
    onFilesSelected,
    cancelUpload,
    deleteAttachment,
    dismissFeedback: () => setFeedback(null),
  };
};
